import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Alert,
} from '@mui/material';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import { useRouteLoaderData } from 'react-router-dom';
import useTenantQuery from '@/hooks/useTenantQuery';
import useTenantMutation from '@/hooks/useTenantMutation';
import useFormData from '@/hooks/useFormData';

export default function TenantTeamsDetail({ data: selectedTeam }) {
  const { users } = useRouteLoaderData('auth');
  const { data: teams } = useTenantQuery('team');

  const { mutateAsync: updateTeam } = useTenantMutation(
    'team',
    selectedTeam?._id,
  );

  const team = teams.find((t) => t._id === selectedTeam._id);

  const initialFormData = useMemo(
    () => ({
      title: selectedTeam?.title || '',
      managers: selectedTeam?.managers || [],
      members: selectedTeam?.members || [],
      type: selectedTeam?.type || null,
    }),
    [team],
  );

  const { formData, dirty, outdated, handleChange, reset, setUpdated } =
    useFormData(initialFormData);

  const availableManagers =
    users?.filter((user) => !formData.members.includes(user._id)) || [];
  const availableMembers =
    users?.filter((user) => !formData.managers.includes(user._id)) || [];

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 200,
      },
    },
  };

  const saveChanges = async () => {
    if (
      !formData.title ||
      formData.managers.length === 0 ||
      formData.members.length === 0
    ) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Alle Felder müssen ausgefüllt werden!"
        />,
      );
      return;
    }

    const teamData = {
      ...formData,
      type: formData.type || null,
    };

    await updateTeam(teamData);
    setUpdated();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        p: 4,
      }}
    >
      {dirty && outdated && (
        <Alert severity="info">
          Die Daten haben sich im Hintergrund geändert!
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          required
          label="Name"
          value={formData.title}
          variant="outlined"
          onChange={handleChange('title')}
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel id="managers-select-label">Manager</InputLabel>
          <Select
            labelId="managers-select-label"
            id="managers-select"
            multiple
            value={formData.managers}
            onChange={handleChange('managers')}
            input={<OutlinedInput label="Manager" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const user = users.find((u) => u._id === id);
                  return (
                    <Chip
                      key={id}
                      label={`${user?.firstName} ${user?.lastName}`}
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {availableManagers
              .slice()
              .sort((a, b) => {
                if (a.firstName < b.firstName) return -1;
                if (a.firstName > b.firstName) return 1;
                return 0;
              })
              .map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {`${user?.firstName} ${user?.lastName}`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="members-select-label">Mitglieder</InputLabel>
          <Select
            labelId="members-select-label"
            id="members-select"
            multiple
            value={formData.members}
            onChange={handleChange('members')}
            input={<OutlinedInput label="Mitglieder" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const user = users.find((u) => u._id === id);
                  return (
                    <Chip
                      key={id}
                      label={`${user?.firstName} ${user?.lastName}`}
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {availableMembers
              .slice()
              .sort((a, b) => {
                if (a.firstName < b.firstName) return -1;
                if (a.firstName > b.firstName) return 1;
                return 0;
              })
              .map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {`${user?.firstName} ${user?.lastName}`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="type-select-label">Typ</InputLabel>
          <Select
            labelId="type-select-label"
            id="type-select"
            value={formData.type || ''}
            onChange={handleChange('type')}
            input={<OutlinedInput label="Typ" />}
          >
            <MenuItem value="">Kein Typ</MenuItem>
            <MenuItem value="ORG_UNIT">Organisationseinheit</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="contained" color="primary" onClick={saveChanges}>
          Speichern
        </Button>
        <Button variant="outlined" color="secondary" onClick={reset}>
          Verwerfen
        </Button>
      </Box>
    </Box>
  );
}
